import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Card, Grid, IconButton, TextField, CircularProgress } from '@mui/material';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { WsButton, WsSelect, WsTypography, WsTypoVariants } from 'wisestep-base';

import { transformToSelectArray, useAutomationMetadata } from '../../utils';

export const ActionComponent = () => {
  const { watch, setValue } = useFormContext();
  const actions = watch('actions') || [];
  const { actionMetadata, actionParameterMap, loading, error, refetch } = useAutomationMetadata();

  const handleAddAction = () => {
    if (Object.keys(actionMetadata).length === 0) return;

    const firstActionType = Object.keys(actionMetadata)[0];
    const actionMeta = actionMetadata[firstActionType];
    const actionParams = actionParameterMap[firstActionType];

    const initialParams: Record<string, any> = {};

    if (actionParams) {
      Object.keys(actionParams).forEach((paramKey) => {
        const param = actionParams[paramKey];
        initialParams[paramKey] = param.defaultValue || '';
      });
    }

    const newAction = {
      id: actionMeta.id,
      actionType: firstActionType,
      params: initialParams,
      actionName: actionMeta.name,
    };

    setValue('actions', [...actions, newAction], { shouldValidate: true, shouldDirty: true });
  };

  const handleRemoveAction = (index: number) => {
    const newActions = [...actions];
    newActions.splice(index, 1);
    setValue('actions', newActions, { shouldValidate: true, shouldDirty: true });
  };

  const handleActionTypeChange = (index: number, newActionType: string) => {
    const selectedAction = actionMetadata[newActionType];
    const selectedActionParams = actionParameterMap[newActionType];

    if (!selectedAction || !selectedActionParams) return;

    const initialParams: Record<string, any> = {};
    Object.keys(selectedActionParams).forEach((paramKey) => {
      const param = selectedActionParams[paramKey];
      initialParams[paramKey] = param.defaultValue || '';
    });

    const newActions = [...actions];
    newActions[index] = {
      ...newActions[index],
      id: selectedAction.id,
      actionType: newActionType,
      params: initialParams,
      actionName: selectedAction.name,
    };

    setValue('actions', newActions, { shouldValidate: true, shouldDirty: true });
  };

  const handleParamChange = (actionIndex: number, paramName: string, value: string) => {
    const newActions = [...actions];

    newActions[actionIndex] = {
      ...newActions[actionIndex],
      params: {
        ...newActions[actionIndex].params,
        [paramName]: value,
      },
    };
    setValue('actions', newActions, { shouldValidate: true, shouldDirty: true });
  };

  const renderInputField = (
    param: any,
    actionIndex: number,
    paramKey: string,
    currentValue: any
  ) => {
    const inputProps: any = {
      name: `actions[${actionIndex}].params.${paramKey}`,
      variant: 'outlined' as const,
      size: 'small' as const,
      fullWidth: true,
      label: param.displayName,
      value: currentValue || '',
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        handleParamChange(actionIndex, paramKey, e.target.value),
      required: param.required,
    };

    switch (param.type?.toLowerCase()) {
      case 'email':
        return <TextField {...inputProps} type="email" />;
      case 'textarea':
        return <TextField {...inputProps} multiline rows={3} />;
      case 'number':
        return <TextField {...inputProps} type="number" />;
      default:
        return <TextField {...inputProps} />;
    }
  };

  const renderParams = (action: any, index: number) => {
    const actionParams = actionParameterMap[action.actionType];

    if (!actionParams) {
      return (
        <Grid item xs={12}>
          <WsTypography>No parameters defined for this action type</WsTypography>
        </Grid>
      );
    }

    const paramKeys = Object.keys(actionParams).sort((a, b) => {
      const numA = parseInt(a.replace(/\D/g, ''), 10);
      const numB = parseInt(b.replace(/\D/g, ''), 10);
      return numA - numB;
    });

    return paramKeys.map((paramKey) => {
      const param = actionParams[paramKey];
      const hasOptions = param.allowedValues && param.allowedValues.length > 0;

      return (
        <Grid item xs={4} key={paramKey}>
          {hasOptions ? (
            <WsSelect
              name={`actions[${index}].params.${paramKey}`}
              selectArray={transformToSelectArray(
                param.allowedValues,
                param.allowedValuesWithLabels
              )}
              label={''}
              defaultValue={action.params[paramKey] || param.defaultValue || ''}
              onChangeValue={(val) => handleParamChange(index, paramKey, val)}
            />
          ) : (
            renderInputField(param, index, paramKey, action.params[paramKey])
          )}
        </Grid>
      );
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
        <WsTypography>Loading action metadata...</WsTypography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <WsTypography>{error}</WsTypography>
        <WsButton onClick={refetch} variant="outlined">
          Retry
        </WsButton>
      </Box>
    );
  }

  return (
    <Box m={1}>
      <WsTypography bold>THEN</WsTypography>

      {actions.length === 0 && (
        <WsTypography variant={WsTypoVariants.BODY1}>No actions added</WsTypography>
      )}

      {actions.map((action, index) => (
        <Card key={`${action.id}-${index}`} sx={{ backgroundColor: '#f5f5f5', mt: 1, p: 1.5 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <WsSelect
              name={`actions[${index}].actionType`}
              selectArray={Object.keys(actionMetadata).map((key) => ({
                label: actionMetadata[key].name || key,
                value: key,
              }))}
              label="Action Type"
              defaultValue={action.actionType || ''}
              onChangeValue={(val) => handleActionTypeChange(index, val)}
            />
            <IconButton onClick={() => handleRemoveAction(index)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <Grid container spacing={2} mt={1}>
            {renderParams(action, index)}
          </Grid>
        </Card>
      ))}

      <Grid mt={2}>
        <WsButton
          startIcon={<AddIcon />}
          variant="contained"
          onClick={handleAddAction}
          disabled={Object.keys(actionMetadata).length === 0}
        >
          Add Action
        </WsButton>
      </Grid>
    </Box>
  );
};
